<template>
  <table class="gvnts-table table table-bordered text-nowrap">
    <thead class="thead-dark">
      <tr>
        <th scope="col" rowspan="2">
          <input type="checkbox" v-model="allSelected" class="mr-2">
          Id
        </th>
        <th scope="col" rowspan="2">Type</th>
        <th scope="col" rowspan="2">Audience</th>
        <th scope="col" rowspan="2">Status</th>
        <th scope="col" rowspan="2">Starts only once</th>
        <th scope="col" colspan="2" class="text-center">Start</th>
        <th scope="col" rowspan="2">Remaining time</th>
        <th scope="col" colspan="2" class="text-center">Finish</th>
        <th scope="col" rowspan="2" v-if="mayManageGvnts">Actions</th>
      </tr>
      <tr>
        <th scope="col">planned</th>
        <th scope="col">real</th>
        <th scope="col">planned</th>
        <th scope="col">real</th>
      </tr>
    </thead>
    <tr
      v-for="gvntContainer in gvntContainers"
      :key="gvntContainer.gvnt.id"
      :class="getGvntClass(gvntContainer.gvnt)"
    >
      <td>
        <input type="checkbox" v-model="gvntContainer.selected" class="mr-2">
        <gvnt-link :gvntId="gvntContainer.gvnt.id" label=""/>
      </td>
      <td class="gvnt-name">
        {{gvntContainer.gvnt.type | getGvntTypeName}}
      </td>
      <td>
        <span v-b-popover.hover.top="getPlayerGroupDescription(gvntContainer.gvnt.playerGroupId)">
          {{gvntContainer.gvnt.playerGroupId | getPlayerGroupName}}
        </span>
      </td>
      <td :class="getGvntStatusClass(gvntContainer.gvnt.status)">
        {{gvntContainer.gvnt.status | getGvntStatusName}}
      </td>
      <td :class="getGvntStartsOnlyOnceClass(gvntContainer.gvnt)">
        {{gvntContainer.gvnt.startsOnlyOnce | getGvntStartsOnlyOnceName}}
      </td>
      <td>
        {{gvntContainer.gvnt.stime | getMomentStr}}
      </td>
      <td>
        {{gvntContainer.gvnt.srtime | getGvntRealStartTimeStr}}
      </td>
      <td>
        {{getRemainingTimeStr(gvntContainer.gvnt)}}
      </td>
      <td>
        {{gvntContainer.gvnt.ftime | getMomentStr}}
      </td>
      <td>
        {{gvntContainer.gvnt.frtime | getGvntRealFinishTimeStr}}
      </td>
      <td v-if="mayManageGvnts" class="action-box">
        <b-button
          v-if="isRunForAllButtonVisible(gvntContainer.gvnt)"
          @click="runGvntForAll(gvntContainer.gvnt)"
          variant="success"
          size="sm"
        >
          Start for all
        </b-button>

        <b-button
          v-if="isFinishButtonVisible(gvntContainer.gvnt.status)"
          @click="finalizeGvnt(gvntContainer.gvnt)"
          variant="warning"
          size="sm"
        >
          Finish
        </b-button>

        <b-button
          @click="deleteGvnt(gvntContainer.gvnt)"
          variant="danger"
          size="sm"
        >
          Delete
        </b-button>

      </td>
    </tr>
  </table>
</template>

<script>

/**
 * @typedef { import('../model/event.js').PlayerGroupId } PlayerGroupId
 */
import { getPlayerGroupName        } from '../model/event.js';
import { getPlayerGroupDescription } from '../model/event.js';

/**
 * @typedef { import('../model/gvnt.js').GvntStatus } GvntStatus
 */
import { getGvntTypeName            } from '../model/gvnt.js';
import { getGvntStatusName          } from '../model/gvnt.js';
import { getGvntStatusClass         } from '../model/gvnt.js';
import { getGvntStartsOnlyOnceName  } from '../model/gvnt.js';
import { getGvntStartsOnlyOnceClass } from '../model/gvnt.js';
import { getGvntRealStartTimeStr    } from '../model/gvnt.js';
import { getGvntRemainingTimeStr    } from '../model/gvnt.js';
import { getGvntRealFinishTimeStr   } from '../model/gvnt.js';
import { isGvntStatusActive         } from '../model/gvnt.js';
import { isGvntStatusClosed         } from '../model/gvnt.js';
import { canRunGvnForAll            } from '../model/gvnt.js';

import GvntLink from './GvntLink.vue';

export default {
  name: 'GvntTable',

  components: {
    GvntLink,
  },

  props: {
    gvntContainers: {
      type: Array,
      required: true,
    },

    nowSec: {
      type: Number,
      required: true,
    },

    mayManageGvnts: {
      type: Boolean,
      required: true,
    },
  },

  computed: {
    allSelected: {
      get() {
        for(const container of this.gvntContainers) {
          if(!container.selected) {
            return false;
          }
        }
        return true;
      },

      set(newValue) {
        if(newValue) {
          this.selectAll();
        } else {
          this.deselectAll();
        }
      },
    },
  },

  methods: {
    /**
     * @param {Object} gvnt
     * @returns {Object}
     */
    getGvntClass(gvnt) {
      return {
        closed: isGvntStatusClosed(gvnt.status),
      };
    },

    getPlayerGroupDescription,
    getGvntStatusClass,
    getGvntStartsOnlyOnceClass,

    /**
     * @param {Object} gvnt
     * @returns {string}
     */
    getRemainingTimeStr(gvnt) {
      return getGvntRemainingTimeStr(gvnt, this.nowSec);
    },

    /**
     * @param {Object} gvnt
     * @returns {boolean}
     */
    isRunForAllButtonVisible(gvnt) {
      if(!this.mayManageGvnts) {
        return false;
      }
      return canRunGvnForAll(gvnt);
    },

    /**
     * @param {Object} gvnt
     */
    runGvntForAll(gvnt) {
      this.$emit('gvnt-run-for-all-requested', gvnt);
    },

    /**
     * @param {GvntStatus} gvntStatus
     * @returns {boolean}
     */
    isFinishButtonVisible(gvntStatus) {
      return isGvntStatusActive(gvntStatus)
    },

    /**
     * @param {Object} gvnt
     */
    finalizeGvnt(gvnt) {
      this.$emit('gvnt-finalization-requested', gvnt);
    },

    /**
     * @param {Object} gvnt
     */
    deleteGvnt(gvnt) {
      this.$emit('gvnt-deletion-requested', gvnt);
    },

    selectAll() {
      this.$emit('gvnts-selection-requested');
    },

    deselectAll() {
      this.$emit('gvnts-deselection-requested');
    },
  },

  filters: {
    getPlayerGroupName,
    getGvntTypeName,
    getGvntStatusName,
    getGvntStartsOnlyOnceName,
    getGvntRealStartTimeStr,
    getGvntRealFinishTimeStr,
  },
};
</script>

<style scoped>
.gvnts-table {
  width: 1px;
}

.gvnt-name {
  font-weight: bold;
}

.closed {
  color: #dcdcdc;
}

.action-box {
  padding: 7px;
  vertical-align: middle;
}

.action-box > .btn:last-child {
  margin-right: 0 !important;
}
</style>
